import throttle from 'lodash.throttle';
import anime from 'animejs/lib/anime.es.js';
import Splitting from 'splitting';

import BaseView from '../views/BaseView';
import Images from './Images';
import Videos from './Videos';
import Audio from './Audio';
import NextChapterOverlay from './NextChapterOverlay';
import Preloader from './Preloader';
import ChapterIndex from '../pollinators/ChapterIndex';
import ChapterPart from './ChapterPart';

export default class Chapter extends BaseView {
	constructor(container) {
		super(container);

		// Index
		this.chapterIndex = ChapterIndex.create(container, this);
	}

	init() {
		this.breakpoint = window.matchMedia('(max-width:767px)');

		// Chapters
		this.currentChapterPartIndex = 0;

		// DOM Elements
		this.elm = this.container.querySelector('[data-chapter]');
		this.content = this.container.querySelector('[data-chapter-content]');

		this.chapterIndexButton = this.container.querySelector('[data-chapter-index-button]');
		this.chapterIndexButton.addEventListener('click', () => {
			this.chapterIndex.toggle();
		});

		// Scoll indicator
		this.scrollIndicator = this.container.querySelector('[data-scroll-indicator]');

		// Parts
		this.preloader = Preloader.create(this);
		this.nextChapterOverlay = NextChapterOverlay.create(this);
		this.audio = Audio.create(this);
		this.images = Images.create(this);
		this.videos = Videos.create(this);
		this.parts = ChapterPart.create(this);

		// Scroll progress
		this.scrollProgress = 0;
		this.boundHandleScroll = throttle(this.handleScroll.bind(this), 50);
		document.addEventListener('scroll', this.boundHandleScroll);

		// Titles
		this.chapterTitle = this.parts[0].partIntrotype.querySelector('[data-chapter-title]');
		if(!this.breakpoint.matches) { // Desktop
			this.splitChapterTitle = Splitting({
				target: this.chapterTitle,
				by: 'chars',
			});
			anime.set(this.splitChapterTitle[0].chars, {
				opacity: 0,
			});
			anime.set(this.chapterTitle, {
				opacity: 1,
			});
		} else { // Mobile
			anime.set(this.chapterTitle, {
				opacity: 0,
			});
		}
		this.chapterNumber = this.parts[0].partIntrotype.querySelector('[data-chapter-number]');

		// Delays
		this.partTitleOutPosition = this.parts[0].partTitleOutPosition;
		this.chapterTitleInDelay = parseInt(this.elm.getAttribute('data-chapter-title-in-delay'));
		this.chapterTitleOutPosition = parseInt(this.elm.getAttribute('data-chapter-title-out-position'));

		this.start();
	}

	getPlatform() {
		if (this.breakpoint.matches === true) {
			return 'mobile';
		}

		return 'desktop';
	}

	async start() {
		window.scrollTo(0, 0);

		await this.preloader.start();

		if (this.videos) {
			this.videos.startObservering();
		}

		this.introduceChapter();
	}

	handleScroll() {

		if (this.lastChapterPartActive) {
			if(this.parts[this.currentChapterPartIndex].lastChapter) {
				this.scrollProgress = (window.scrollY / (document.documentElement.scrollHeight - document.documentElement.clientHeight - this.parts[this.currentChapterPartIndex].nextPartScroll.clientHeight)) * 100;
			} else {
				this.scrollProgress = (window.scrollY / (document.documentElement.scrollHeight - document.documentElement.clientHeight)) * 100;
			}
		} else {
			this.scrollProgress = (window.scrollY / (document.documentElement.scrollHeight - document.documentElement.clientHeight - this.parts[this.currentChapterPartIndex].nextPartScroll.clientHeight)) * 100;
		}

		if(this.scrollProgress>100) {
			this.scrollProgress = 100;
		}
		this.container.style.setProperty('--scroll-progress', `${this.scrollProgress}%`);

		if (this.scrollProgress > this.chapterTitleOutPosition) {
			this.hideChapterTitle();
		}

		if (this.scrollProgress > this.partTitleOutPosition) {
			this.hidePartTitle();
		}
	}

	get lastChapterPartActive() {
		return this.parts.length - 1 == this.currentChapterPartIndex;
	}

	async goToPart(index) {
		if (index >= this.parts.length) {
			return false;
		}

		this.audio.pause();
		anime.set(this.audio.scrollProgressElement, {
			display: 'none',
		});
		anime.set(this.audio.progressBar, {
			display: 'none',
		});

		this.audio.src = this.parts[index].data.object_url;
		this.parts[this.currentChapterPartIndex].hide();
		this.parts[index].init();
		this.currentChapterPartIndex = index;

		this.audio.moveProgressBar(index);
		this.audio.activateSubtitles(index);

		await this.audio.play();
		anime.set(this.audio.scrollProgressElement, {
			display: 'block',
		});
		anime.set(this.audio.progressBar, {
			display: 'block',
		});

		this.partTitleOutPosition = this.parts[index].partTitleOutPosition;
	}

	hideChapterTitle() {
		this.chapterTitle.classList.add('is-hidden');
		this.chapterNumber.classList.remove('is-shown');
	}

	hidePartTitle() {
		this.parts[this.currentChapterPartIndex].hideTitles();
	}

	introduceChapter() {

		// Zoom out Modules
		if(!this.breakpoint.matches) {
			anime.set(this.elm, {
				overflow: 'hidden',
			});

			anime.set(this.parts[0].el, {
				transformOrigin: 'top center',
				scale: 1.2,
				opacity: 0,
			});
			anime({
				targets: this.parts[0].el,
				scale: 1,
				opacity: 1,
				easing: 'easeInOutSine',
				duration: 5000,
				delay: 0,
				complete: () => {
					anime.set(this.elm, {
						overflow: 'unset',
					});
				},
			});

		}

		// Show Title
		if(!this.breakpoint.matches) { // Desktop
			anime({
				targets: this.splitChapterTitle[0].chars,
				opacity: 1,
				duration: 1,
				easing: 'linear',
				delay: anime.stagger(100, { start: this.chapterTitleInDelay }),
				begin: () => {
					const characterCount = this.splitChapterTitle[0].chars.length;
					setTimeout(() => {
						// Show Chapter Number, finish at the same time as the chapter title
						this.chapterNumber.classList.add('is-shown');
					}, this.chapterTitleInDelay + (characterCount - 5) * 100);
				},
				complete: () => {
					this.elm.classList.add('show-header-ui');
					setTimeout(() => {
						this.parts[0].showTitles();
					}, 1000);
				},
			});
		} else { // Mobile
			anime({
				targets: this.chapterTitle,
				opacity: 1,
				duration: 1000,
				delay: 2000,
				easing: 'linear',
				begin: () => {
					setTimeout(() => {
						this.chapterNumber.classList.add('is-shown');
					}, 1000);
				},
				complete: () => {
					this.elm.classList.add('show-header-ui');
					setTimeout(() => {
						this.parts[0].showTitles();
					}, 1000);
				},
			});
		}
	}

	goToNextPart() {
		if (this.lastChapterPartActive) {
			return;
		}

		this.goToPart(this.currentChapterPartIndex + 1);
	}

	goToPreviousPart() {
		if (this.currentChapterPartIndex == 0) {
			return;
		}

		this.goToPart(this.currentChapterPartIndex - 1);
	}

	destroy() {
		super.destroy();

		if (this.audio) {
			this.audio.destroy();
		}

		if (this.video) {
			this.videos.destroy();
		}

		if (this.nextChapterOverlay) {
			this.nextChapterOverlay.destroy();
		}

		document.removeEventListener('scroll', this.boundHandleScroll);

		if(this.parts) {
			this.parts.forEach(part =>{
				part.destroy();
			});
		}
	}
}
